/** Shared onboarding-wizard primitives — Stepper / Card / Field / NavRow.
 *  Used by all 4 org-type wizards (Production House · Trade Org · Studio · Vendor).
 *  Keeps each wizard file focused on the type-specific middle step. */
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowRight, ArrowLeft, Check, Loader2 } from "lucide-react";

export function Stepper({ steps, step }) {
  return (
    <div className="mt-8 mb-8 flex items-center gap-2.5 flex-wrap" data-testid="onboard-stepper">
      {steps.map((label, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <div key={label} className="flex items-center gap-2.5">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold ${
              done ? "bg-emerald-500/25 text-emerald-300"
              : active ? "bg-[var(--association-theme)] text-black"
              : "bg-white/10 text-white/55"
            }`}>
              {done ? <Check size={12} /> : i + 1}
            </div>
            <div className={`text-[10px] tracking-[0.22em] uppercase ${active ? "text-white" : "text-white/45"}`}>
              {label}
            </div>
            {i < steps.length - 1 && <div className="w-6 h-px bg-white/15" />}
          </div>
        );
      })}
    </div>
  );
}

export function Card({ title, children, testid }) {
  return (
    <div className="cs-card rounded-2xl p-6 space-y-5" data-testid={testid}>
      {title && (
        <div className="text-[10px] tracking-[0.25em] uppercase text-white/55">{title}</div>
      )}
      {children}
    </div>
  );
}

export function Field({ label, children, tid }) {
  return (
    <div data-testid={tid}>
      <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

export function NavRow({ onBack, onNext, onSubmit, submitting, submitLabel, disabledNext }) {
  return (
    <div className="flex items-center justify-between pt-3">
      {onBack ? (
        <Button onClick={onBack} variant="ghost"
          className="h-10 px-4 text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white"
          data-testid="onboard-back">
          <ArrowLeft size={13} className="mr-1.5" /> Back
        </Button>
      ) : <span />}
      {onSubmit ? (
        <Button onClick={onSubmit} disabled={disabledNext || submitting}
          className="btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center"
          data-testid="onboard-submit">
          {submitting && <Loader2 size={14} className="mr-2 animate-spin" />}
          {submitLabel || "Submit"} <ArrowRight size={14} className="ml-2" />
        </Button>
      ) : (
        <Button onClick={onNext} disabled={disabledNext}
          className="btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center"
          data-testid="onboard-next">
          Continue <ArrowRight size={14} className="ml-2" />
        </Button>
      )}
    </div>
  );
}
